import { getUtm } from "@/utils/utm";
import { decodeChapterListUrl } from "./URL";
import { getReferrer } from "./referrer";
import qs from "qs";
import Cookie from "js-cookie";
import { isIOS } from "./device";
import type { NextRouter } from "next/router";

// https://support.appsflyer.com/hc/en-us/articles/207032246-OneLink-1-4-Converting-users-from-owned-media#what-are-the-limitations-of-onelink-urls
// af 相对于默认的 encodeURIComponent 额外对 *'!~ 四个符号敏感
const afLinkCompatible = (link: string) => link.replace(/[*'!~]/g, "");

const SCHEMA = "chereads";
const BASE_LINK = "chereads.onelink.me/DMfE";

export enum PIDS {
  default = "ref_chereads_site",
  ad = "ref_chereads_ad",
}

export enum AF_ADSET {
  header_download = "header_download",
  float_qrcode = "float_qrcode",
}

interface IOpenAppType {
  router: NextRouter;
  pid?: string;
  afQuery?: {
    af_adset?: AF_ADSET;
  };
}

export const openApp = ({
  router,
  pid = PIDS.default,
  afQuery,
}: IOpenAppType) => {
  const { appLink, otherQuery } = getOneLinkParams(router, afQuery);
  const link = _getLink(appLink, pid, otherQuery);
  if (link) {
    window.location.href = link;
  }
};

export const getDownloadLink = ({
  router,
  pid = PIDS.default,
  afQuery,
}: IOpenAppType) => {
  const { appLink, otherQuery } = getOneLinkParams(router, afQuery);
  return _getLink(appLink, pid, otherQuery) || "#";
};

/**
 * 打开或下载APP
 * @param link app 中的打开路径，参考 https://doc.weixin.qq.com/sheet/e3_m_egmLeTpJFQJM?scode=AFoA2wfSABEiUMaXIrAMgAjwbFALw
 * @param pid 转换为端的参数 media_source
 * @param otherQuery 其他参数, start with "&"
 */
function _getLink(
  link: string | undefined,
  pid: string = PIDS.default,
  otherQuery: string = ""
) {
  if (typeof window !== "undefined") {
    // adjust pid, 如果 utm 当中 utm_media=google or facebook，则将 pid = MSiteAdRef
    // http://tapd.oa.com/QidianOverSeas/prong/stories/view/1110143901858182106
    const utm = getUtm();
    if (utm && (utm.utm_media === "facebook" || utm.utm_media === "google")) {
      pid = PIDS.ad;
    }
    let linkInApp = "";
    if (link) {
      const query = encodeURIComponent(JSON.stringify({ url: link }));
      linkInApp = `/open?query=${query}`;
    }
    const APP_SCHEME = `${SCHEMA}://app${linkInApp}`;

    if (isIOS()) {
      return afLinkCompatible(
        `https://${BASE_LINK}?pid=${pid}&af_web_dp=https%3A%2F%2Fwww.chereads.com%2F&is_retargeting=true&af_force_deeplink=true${
          link ? `&af_dp=${encodeURIComponent(APP_SCHEME)}` : ""
        }${otherQuery}`
      );
    }
    return afLinkCompatible(
      `https://${BASE_LINK}?pid=${pid}&af_web_dp=https%3A%2F%2Fwww.chereads.com%2F&is_retargeting=true${
        link ? `&af_dp=${encodeURIComponent(APP_SCHEME)}` : ""
      }${otherQuery}`
    );
  }
}

/**
 * 根据当前路由映射app侧的路由 和 一些 af 参数
 * @param router
 * @param afQuery
 */
function getOneLinkParams(router: NextRouter, afQuery = {}) {
  let otherQuery;
  let appLink = "/store"; // 默认进入app书城
  const utm = getUtm();
  const { pathname, asPath } = router;
  if (pathname === "/") {
    otherQuery = { af_c_id: "home_page" };
  } else if (pathname === "/chapterlist/[bookId]") {
    const bookId = decodeChapterListUrl(asPath);
    appLink = `/book/${bookId}`;
    otherQuery = { af_c_id: "book", af_ad: bookId };
  } else if (pathname === "/manga-chapterlist/[bookId]") {
    const bookId = decodeChapterListUrl(asPath);
    appLink = `/comic/${bookId}`;
    otherQuery = { af_c_id: "comic", af_ad: bookId };
  } else {
    otherQuery = { af_c_id: "others" };
  }

  return {
    appLink,
    otherQuery: `&${qs.stringify({
      af_sub4: `uuid=${Cookie.get("webnovel_uuid") || ""}&platform=cr`,
      ...otherQuery,
      ...afQuery,
      ...(getReferrer()
        ? {
            af_channel: getReferrer(),
          }
        : {}),
      // adjust pid, 如果 utm 当中 utm_media=google or facebook，则将 pid = MSiteAdRef
      // http://tapd.oa.com/QidianOverSeas/prong/stories/view/1110143901858182106
      ...(utm &&
      (utm.utm_media === "facebook" || utm.utm_media === "google") &&
      utm.utm_source
        ? {
            c: utm.utm_source,
          }
        : {}),
    })}`,
  };
}
